import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import Avatar from '@mui/material/Avatar'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import { Iconify } from '@/lib/iconify'
import { BlynIcon } from './BlynIcon'
import { store } from './legacy/state/store'
import { useSettingsContext } from '@/lib/settings'

const NAV_ITEMS = [
  { view: 'new-conversation', icon: 'plus',             label: 'New Conversation · ⌘N', isNew: true },
  // { view: 'conversation',     icon: 'message',          label: 'Conversation' },
  { view: 'projects',         icon: 'folder',           label: 'Projects' },
  { view: 'tasks',            icon: 'zap',              label: 'Running Tasks' },
  { view: 'creatives',        icon: 'mail',             label: 'Creatives' },
  { view: 'web-personalization', icon: 'globe',         label: 'Web Personalization' },
  { view: 'product-catalog',  icon: 'package',          label: 'Product Catalog' },
  { view: 'knowledge',        icon: 'book',             label: 'Knowledge Base' },
  { view: 'forecast',         icon: 'trending',         label: 'Insights & Forecasts' },
  { view: 'staged',           icon: 'staged',           label: 'Staged Actions' },
  { view: 'approvals',        icon: 'approvals',        label: 'Approvals Queue' },
  { view: 'nudges',           icon: 'bell',             label: 'Nudges', pip: true },
  { view: 'notifications',    icon: 'notifications',    label: 'Notifications' },
]

export default function NavRail() {
  const location = useLocation()
  const navigate = useNavigate()
  const settings = useSettingsContext()
  const [userInitial, setUserInitial] = useState<string>('T')
  const [expanded, setExpanded] = useState(() => {
    const stored = localStorage.getItem('nav-expanded') === 'true'
    document.documentElement.style.setProperty('--blyn-nav-w', stored ? '200px' : '56px')
    return stored
  })

  useEffect(() => {
    const u = store.get('currentUser') as { initial?: string } | null
    if (u?.initial) setUserInitial(u.initial)
    const unsub = store.select('currentUser', (v: { initial?: string }) => {
      if (v?.initial) setUserInitial(v.initial)
    })
    return () => {
      unsub?.()
    }
  }, [])

  useEffect(() => {
    document.documentElement.style.setProperty('--blyn-nav-w', expanded ? '200px' : '56px')
    localStorage.setItem('nav-expanded', String(expanded))
  }, [expanded])

  function isActive(view: string): boolean {
    const base = `/blyn/${view}`
    return location.pathname === base || location.pathname.startsWith(base + '/')
  }

  function toggleTheme() {
    const next = settings.colorScheme === 'dark' ? 'light' : 'dark'
    window.dispatchEvent(new CustomEvent('blyn:theme-change-request', { detail: { theme: next } }))
  }

  const isDark = settings.colorScheme === 'dark'

  return (
    <Stack
      component="nav"
      aria-label="Primary navigation"
      className="nav-rail"
      data-expanded={expanded ? 'true' : 'false'}
      alignItems={expanded ? 'stretch' : 'center'}
      gap={0.5}
    >
      <Box className="nav-rail-top" sx={{ display: 'flex', alignItems: 'center', justifyContent: expanded ? 'space-between' : 'center', width: '100%', flexShrink: 0 }}>
        {expanded && <Box className="brand-mark">Blyn</Box>}
        <Tooltip title={expanded ? 'Collapse sidebar' : 'Expand sidebar'} placement="right" arrow>
          <IconButton
            className="nav-item nav-toggle desktop-only-toggle"
            aria-label={expanded ? 'Collapse navigation' : 'Expand navigation'}
            onClick={() => setExpanded(e => !e)}
          >
            <BlynIcon name={expanded ? 'sidebarCollapse' : 'sidebarExpand'} size={18} />
          </IconButton>
        </Tooltip>
      </Box>

      <Box className="nav-rail-items">
        {NAV_ITEMS.map(item => (
          <Tooltip key={item.view} title={item.label} placement="right" arrow disableHoverListener={expanded}>
            <IconButton
              className={`nav-item${item.isNew ? ' nav-new' : ''}`}
              data-active={isActive(item.view) ? 'true' : 'false'}
              aria-label={item.label}
              onClick={() => navigate(`/blyn/${item.view}`)}
            >
              <BlynIcon name={item.icon} size={18} />
              <span className="nav-label">{item.label.split(' ·')[0]}</span>
              {item.pip && <Box component="span" className="nudge-pip" />}
            </IconButton>
          </Tooltip>
        ))}
      </Box>

      <Box className="nav-rail-bottom">
        <Tooltip title={expanded ? '' : 'Settings'} placement="right" arrow disableHoverListener={expanded}>
          <IconButton
            className="nav-item"
            data-active={isActive('settings') ? 'true' : 'false'}
            aria-label="Settings"
            onClick={() => navigate('/blyn/settings')}
          >
            <BlynIcon name="settings" size={18} />
            <span className="nav-label">Settings</span>
          </IconButton>
        </Tooltip>

      </Box>
    </Stack>
  )
}

/**
 * Same nav destinations as the desktop icon rail, rendered as a plain list
 * for the mobile drawer — used instead of `.nav-rail`'s markup/CSS since
 * that's scoped to the always-icon-only rail and isn't meant to render
 * full-width with labels outside of it.
 */
export function MobileNavList({ onNavigate }: { onNavigate?: () => void }) {
  const location = useLocation()
  const navigate = useNavigate()

  function isActive(view: string): boolean {
    const base = `/blyn/${view}`
    return location.pathname === base || location.pathname.startsWith(base + '/')
  }

  function go(view: string) {
    navigate(`/blyn/${view}`)
    onNavigate?.()
  }

  const itemSx = {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    width: '100%',
    justifyContent: 'flex-start',
    borderRadius: 'var(--r-sm)',
    padding: '9px 12px',
    color: 'var(--text-primary)',
    fontSize: 13,
  } as const

  return (
    <Stack sx={{ p: 1, gap: 0.5 }}>
      {NAV_ITEMS.map(item => (
        <IconButton
          key={item.view}
          data-active={isActive(item.view) ? 'true' : 'false'}
          aria-label={item.label}
          onClick={() => go(item.view)}
          sx={{
            ...itemSx,
            bgcolor: isActive(item.view) ? 'var(--accent-bg)' : 'transparent',
            '&:hover': { bgcolor: 'var(--bg-hover)' },
          }}
        >
          <BlynIcon name={item.icon} size={18} />
          <Box component="span" sx={{ flex: 1, textAlign: 'left' }}>{item.label.split(' ·')[0]}</Box>
          {item.pip && <Box component="span" className="nudge-pip" />}
        </IconButton>
      ))}
      <IconButton
        data-active={isActive('settings') ? 'true' : 'false'}
        aria-label="Settings"
        onClick={() => go('settings')}
        sx={{
          ...itemSx,
          bgcolor: isActive('settings') ? 'var(--accent-bg)' : 'transparent',
          '&:hover': { bgcolor: 'var(--bg-hover)' },
        }}
      >
        <BlynIcon name="settings" size={18} />
        <Box component="span" sx={{ flex: 1, textAlign: 'left' }}>Settings</Box>
      </IconButton>
    </Stack>
  )
}
